import { useEffect, useRef } from "react";
import { Map as MLMap, Marker as MLMarker, Popup as MLPopup, NavigationControl, setWorkerUrl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

setWorkerUrl("/maplibre-gl-worker.mjs");

const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

export default function MapView({ center, zoom, markers = [], path = [], follow = null, className }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerObjs = useRef([]);

  useEffect(() => {
    const map = new MLMap({
      container: containerRef.current,
      style: STYLE_URL,
      center: [center.lng, center.lat],
      zoom,
      attributionControl: { compact: true },
    });
    map.addControl(new NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markerObjs.current.forEach(m => m.remove());
    markerObjs.current = markers.map(m => {
      let mk;
      if (m.label) {
        const el = document.createElement("div");
        el.textContent = m.label;
        el.setAttribute("data-testid", "map-price-marker");
        el.style.cssText = `background:${m.color || "#386d54"};color:#fff;font-weight:700;font-size:13px;padding:4px 10px;border-radius:9999px;box-shadow:0 2px 8px rgba(0,0,0,.3);border:2px solid #fff;white-space:nowrap;cursor:pointer;font-family:inherit`;
        mk = new MLMarker({ element: el }).setLngLat([m.lng, m.lat]);
      } else {
        mk = new MLMarker({ color: m.color || "#386d54" }).setLngLat([m.lng, m.lat]);
      }
      if (m.popupHtml) mk.setPopup(new MLPopup({ offset: 28, closeButton: false }).setHTML(m.popupHtml));
      mk.addTo(map);
      return mk;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(markers)]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || path.length < 2) return;
    const geojson = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: path.map(p => [p.lng, p.lat]) },
    };
    const apply = () => {
      if (map.getSource("route")) {
        map.getSource("route").setData(geojson);
      } else {
        map.addSource("route", { type: "geojson", data: geojson });
        map.addLayer({
          id: "route",
          type: "line",
          source: "route",
          paint: { "line-color": "#386d54", "line-width": 4, "line-opacity": 0.8 },
          layout: { "line-cap": "round", "line-join": "round" },
        });
      }
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(path)]);

  useEffect(() => {
    if (mapRef.current && follow) mapRef.current.panTo([follow.lng, follow.lat]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [follow?.lat, follow?.lng]);

  return <div ref={containerRef} className={className || "h-full w-full"} data-testid="map-view" />;
}
